import { PositionTypeEnum, useFeatureFlag, useRoleAuth } from '@src/hooks';
import { pinClosePositionColumnColDefParams, useTradePositionColumn } from '@src/utils/columns/useTradePositionColumn';
import {
  ACTION,
  CopyCsv,
  CopyJson,
  Divider,
  IconName,
  LedgerAccountTypeEnum,
  MixpanelEventSource,
  PositionUpdateSourceEnum,
  ShowJson,
  agGridMenuSeparator,
  filterByCellValueMenuItem,
  filterByColumnMainMenuItems,
  getRowNodesToOperateOn,
  resolveShowJsonMenuData,
  useCallbackRef,
  useDisclosure,
  useJsonModal,
  useMixpanel,
  useRelatedEntitiesLookupMaps,
  useSyncedRef,
  type AgGridMenuItems,
  type ColumnDef,
  type ContextMenuOptions,
  type FilterableProperty,
  type Position,
  type RelatedEntitiesConfig,
  type UseFilterBuilderOutput,
} from '@talos/kyoko';
import type {
  GetContextMenuItemsParams,
  GetMainMenuItemsParams,
  ICellRendererParams,
  MenuItemDef,
} from 'ag-grid-community';
import { compact, isNil } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { BulkCloseDialog } from './BulkClosePosition/BulkCloseDialog';

import { useGetOpenTransactionsMenuItems } from '@src/containers/Trading/Markets/AccountLedgerEvents/useGetOpenTransactionsMenuItems';
import { useLegacyUpdateBalancesDialog } from '../BalancesV2/useLegacyUpdateBalancesDialog';
import { useUpdateBalancesDialog } from '../BalancesV2/useUpdateBalancesDialog';
import { useGetBulkClosablePositions, type ClosablePosition } from './BulkClosePosition/useGetBulkClosablePositions';
import { getPositionsRelatedEntitiesConfig } from './positionsRelatedDocuments';
import { colIDToFilterBuilderKey, type PositionsTabType } from './types';
import { UpdatePositionsDialog } from './UpdatePositionsDialog';

export function PositionMenu<T extends Position>(
  params: ICellRendererParams<T> & {
    onShowJson(data: unknown): void;
    relatedEntities: RelatedEntitiesConfig<T>;
  }
) {
  const { onShowJson, relatedEntities, ...cellParams } = params;
  const lookupMaps = useRelatedEntitiesLookupMaps();

  const handleShowJson = (data: T | T[]) => {
    onShowJson(resolveShowJsonMenuData(data, cellParams.node?.data, relatedEntities, lookupMaps));
  };

  return (
    <>
      <ShowJson {...cellParams} onShowJson={handleShowJson} />
      <Divider orientation="horizontal" />
      <CopyJson {...cellParams} />
      <CopyCsv {...cellParams} />
    </>
  );
}

interface UsePositionsMenuParams {
  openClause: UseFilterBuilderOutput['addAndOpenClause'] | undefined;
  filterableProperties: FilterableProperty<string>[];
  enableBulkClose?: boolean;
  tabType: PositionsTabType;

  /**
   * An optional callback you can implement in order to take control of the resolving of a right-click in a column
   * to the colId that should be used for filtering on the right-clicked column.
   *
   * The provided function returning undefined is effectively the same as the function itself not being provided -- column filtering
   * fallbacks to its standard code path.
   *
   * This is needed for supporting Tree Blotters.
   */
  getColIdForFiltering?: (params: GetContextMenuItemsParams) => string | undefined;
  /**
   * Whether or not to show the Open transactions action button in the context menu. Defaults to false.
   */
  showOpenTransactionsMenuItem?: boolean;
  /**
   * Whether or not to show the Close position column. Defaults to true if user is authorized to trade.
   */
  showClosePositionColumn?: boolean;
}

export function usePositionsMenu<T extends Position>({
  openClause,
  filterableProperties,
  enableBulkClose = false,
  tabType,
  getColIdForFiltering,
  showOpenTransactionsMenuItem,
  showClosePositionColumn,
}: UsePositionsMenuParams) {
  const bulkClosePositionsDialog = useDisclosure();
  const mixpanel = useMixpanel();
  const { handleClickJson, jsonModal } = useJsonModal();
  const {
    enableBulkClosePosition: enableBulkClosePositionFeatureFlag,
    enableLegacyUpdateBalances,
    enableSEDOLDisplay,
  } = useFeatureFlag();
  const relatedEntities = useMemo(
    (): RelatedEntitiesConfig<T> => getPositionsRelatedEntitiesConfig(enableSEDOLDisplay),
    [enableSEDOLDisplay]
  );

  const { isAuthorized } = useRoleAuth();
  const authorizedToTrade = isAuthorized(ACTION.SUBMIT_ORDER);
  const authorizedToEditPosition = isAuthorized(ACTION.EDIT_POSITION);

  const showBulkClose = enableBulkClose && enableBulkClosePositionFeatureFlag;

  const { getBulkClosablePositions } = useGetBulkClosablePositions();
  // We make this function stable so we don't do too much work and risk changing the suggested SendOrderArgs returned mid-confirmation.
  const stableGetBulkClosablePositions = useSyncedRef(getBulkClosablePositions);

  // State representing the positions set to be closed as their respective SendOrderArgs action representations
  const [closablePositions, setClosablePositions] = useState<ClosablePosition[]>([]);

  const [editPositionPrimer, setEditPositionPrimer] = useState<Position>();
  const updatePositionsDialog = useDisclosure({ onClose: () => setEditPositionPrimer(undefined) });

  const { open: updatePositionsDialogOpen } = updatePositionsDialog;
  const openUpdatePositionsDialog = useCallback(
    (primer?: Position) => {
      setEditPositionPrimer(primer);
      updatePositionsDialogOpen();
    },
    [updatePositionsDialogOpen]
  );

  const {
    legacyUpdateBalancesDialogComponent,
    getLegacyUpdateBalanceContextMenuItem,
    openLegacyUpdateBalancesDialog,
    showLegacyUpdateBalancesButton,
  } = useLegacyUpdateBalancesDialog<T>({
    enabled: enableLegacyUpdateBalances,
    getMarketAccount: getPositionMarketAccount,
    getAsset: getPositionAsset,
    blotterMarketAccountField: 'MarketAccount' satisfies keyof Position,
  });

  const {
    updateBalancesDialogComponent: adjustMarketAccountPositionsDialogComponent,
    getUpdateBalanceContextMenuItem: getAdjustMarketAccountContextMenuItem,
    openUpdateBalancesDialog: openAdjustMarketAccountPositionsDialog,
    showUpdateBalancesButton: showAdjustMarketAccountPositionsButton,
  } = useUpdateBalancesDialog<T>({
    enabled: !enableLegacyUpdateBalances,
    getMarketAccount: getPositionMarketAccount,
    getAsset: getPositionAsset,
    getAvgCost: getPositionAvgCost,
    getAvgCostCurrency: getPositionAvgCostCurrency,
    blotterMarketAccountField: 'MarketAccount' satisfies keyof Position,
    requireBalancePublishForContextMenu: false,
  });

  const { open: bulkClosePositionsDialogOpen } = bulkClosePositionsDialog;
  const openBulkClosePositionDialog = useCallback(
    (closablePositions: ClosablePosition[]) => {
      setClosablePositions(closablePositions);
      bulkClosePositionsDialogOpen();
    },
    [bulkClosePositionsDialogOpen]
  );

  const getOpenTransactionsMenuItems = useGetOpenTransactionsMenuItems();
  const getCustomContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<T>): AgGridMenuItems<T> => {
    const newSelectedPositions = compact(getRowNodesToOperateOn(params).map(node => node.data));

    const data = params?.node?.data;
    const groupData = params?.node?.groupData;
    if (isNil(data) && isNil(groupData)) {
      return [];
    }

    // Filter → separator → Close → separator → Adjust/Edit → separator → Open… → (Kyoko V2 defaults)
    const closeItems: AgGridMenuItems<T> = [];
    const filterItems: AgGridMenuItems<T> = [];
    const adjustItems: AgGridMenuItems<T> = [];
    const openTransactionsItems: AgGridMenuItems<T> = [];

    if (showBulkClose && authorizedToTrade && newSelectedPositions.length > 0) {
      const closeOrderArgs = stableGetBulkClosablePositions.current(newSelectedPositions);
      if (closeOrderArgs.length > 0) {
        closeItems.push({
          name: `Close ${closeOrderArgs.length} position(s)`,
          action: () => openBulkClosePositionDialog(closeOrderArgs),
          icon: `<i class="ag-icon ${IconName.Close}"/>`,
        });
      }
    }

    if (openClause) {
      // if a manual resolver is passed, use it. if not, colID is undefined, which will cause filterByCellValueItem to do its default thing
      const colID = getColIdForFiltering?.(params);

      // Strip the helper's trailing separator; we join groups ourselves below.
      const filterMenuItems = filterByCellValueMenuItem({
        params,
        filterableProperties,
        openClause,
        colIDToFilterBuilderKey,
        mixpanel,
        colID,
      });
      filterItems.push(
        ...filterMenuItems.filter(
          (item): item is MenuItemDef<T> => item !== agGridMenuSeparator && item !== 'separator'
        )
      );
    }

    if (authorizedToEditPosition && data != null && data.PositionSource === PositionUpdateSourceEnum.Internal) {
      if (tabType === 'Sub Accounts') {
        adjustItems.push({
          name: 'Adjust Sub Account Position',
          action: () => openUpdatePositionsDialog(data),
          icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
        });
      } else if (enableLegacyUpdateBalances && isMarketAccountPositionsTab(tabType)) {
        adjustItems.push({
          name: 'Edit position',
          action: () => openUpdatePositionsDialog(data),
          icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
        });
      }
    }

    const updateBalancesItem = enableLegacyUpdateBalances
      ? getLegacyUpdateBalanceContextMenuItem(params)
      : getAdjustMarketAccountContextMenuItem(params);
    if (updateBalancesItem && isMarketAccountPositionsTab(tabType)) {
      adjustItems.push(updateBalancesItem);
    }

    if (showOpenTransactionsMenuItem && data != null) {
      const accountType =
        tabType === 'Sub Accounts' ? LedgerAccountTypeEnum.SubAccount : LedgerAccountTypeEnum.MarketAccount;
      const asset = data.Asset;
      const account = tabType === 'Sub Accounts' ? data.SubAccount : data.MarketAccount;
      openTransactionsItems.push(
        ...getOpenTransactionsMenuItems({
          asset,
          account,
          accountType,
          source: MixpanelEventSource.PositionsBlotter,
          showAccountOption: true,
        })
      );
    }

    return joinMenuGroups([filterItems, closeItems, adjustItems, openTransactionsItems]);
  });

  const getExtraMainMenuItems = useCallbackRef((params: GetMainMenuItemsParams) => {
    if (!openClause) {
      return [];
    }
    return filterByColumnMainMenuItems({
      params,
      colIDToFilterBuilderKey,
      openClause,
      mixpanel,
    });
  });

  const dialogs = useMemo(
    () => (
      <>
        <BulkCloseDialog closablePositions={closablePositions} dialog={bulkClosePositionsDialog} />
        {(tabType === 'Sub Accounts' || enableLegacyUpdateBalances) && (
          <UpdatePositionsDialog
            dialogProps={updatePositionsDialog}
            positionPrimer={editPositionPrimer}
            positionType={
              tabType === 'Sub Accounts' ? PositionTypeEnum.SubAccountPosition : PositionTypeEnum.MarketAccountPosition
            }
          />
        )}
        {legacyUpdateBalancesDialogComponent}
        {adjustMarketAccountPositionsDialogComponent}
        {jsonModal}
      </>
    ),
    [
      jsonModal,
      closablePositions,
      bulkClosePositionsDialog,
      updatePositionsDialog,
      editPositionPrimer,
      tabType,
      enableLegacyUpdateBalances,
      legacyUpdateBalancesDialogComponent,
      adjustMarketAccountPositionsDialogComponent,
    ]
  );

  // We want to constrain the security filtering to market account if we're in any other blotter than the sub account one,
  // because on those records we don't have any market accounts, so need to keep it loose.
  const constrainSecuritiesToMarket = tabType !== 'Sub Accounts';
  const closePositionColumn = useTradePositionColumn<Position>({
    assetField: 'Asset',
    action: 'Close',
    columnId: 'closePosition',
    getAmount: getPositionAmount,
    colDefParams: pinClosePositionColumnColDefParams,
    getAssetType: getPositionAssetType,
    marketAccountField: 'MarketAccount',
    subAccountField: 'SubAccount',
    constrainSecuritiesToMarket,
  });

  const shouldShowClosePositionColumn =
    showClosePositionColumn !== undefined ? showClosePositionColumn : authorizedToTrade;

  const columns = useMemo(
    () =>
      compact([
        shouldShowClosePositionColumn ? closePositionColumn : undefined,
        {
          type: 'hamburgerMenu',
          id: 'rowMenu',
          params: {
            renderItems: params => (
              <PositionMenu {...params} onShowJson={handleClickJson} relatedEntities={relatedEntities} />
            ),
          },
        },
      ]) satisfies ColumnDef<Position>[],
    [handleClickJson, closePositionColumn, shouldShowClosePositionColumn, relatedEntities]
  );

  const contextMenuOptions = useMemo(
    (): ContextMenuOptions<T> => ({
      getCustomContextMenuItems,
      relatedEntities,
    }),
    [getCustomContextMenuItems, relatedEntities]
  );

  return {
    columns,
    getCustomContextMenuItems,
    relatedEntities,
    contextMenuOptions,
    getExtraMainMenuItems,
    dialogs,
    onClickJson: handleClickJson,
    openUpdatePositionDialog: updatePositionsDialog.open,
    openUpdateBalancesDialog: enableLegacyUpdateBalances
      ? openLegacyUpdateBalancesDialog
      : openAdjustMarketAccountPositionsDialog,
    showUpdateBalancesButton: enableLegacyUpdateBalances
      ? showLegacyUpdateBalancesButton
      : showAdjustMarketAccountPositionsButton,
    openBulkClosePositionDialog,
  };
}

/** Joins non-empty menu groups with a single separator between each group. */
function joinMenuGroups<TData>(groups: AgGridMenuItems<TData>[]): AgGridMenuItems<TData> {
  const result: AgGridMenuItems<TData> = [];
  for (const group of groups) {
    if (group.length === 0) {
      continue;
    }
    if (result.length > 0) {
      result.push(agGridMenuSeparator);
    }
    result.push(...group);
  }
  return result;
}

function isMarketAccountPositionsTab(tabType: PositionsTabType): boolean {
  switch (tabType) {
    case 'Derivatives':
    case 'Unified':
    case 'Predictions':
      return true;
    case 'Sub Accounts':
      return false;
    default: {
      const _exhaustive: never = tabType;
      return _exhaustive;
    }
  }
}

function getPositionAssetType(position: Position) {
  return position.AssetType;
}

function getPositionMarketAccount(position: Position) {
  return position.MarketAccount;
}

function getPositionAsset(position: Position) {
  return position.Asset;
}

function getPositionAvgCost(position: Position) {
  return position.AvgCost;
}

function getPositionAvgCostCurrency(position: Position) {
  return position.AvgCostCurrency;
}

function getPositionAmount(position: Position) {
  return position.Amount;
}
